import React from 'react';
import cx from 'classnames';
import { Chip } from 'components';

import './CategoryBadge.scss';

export interface CategoryBadgeCategory {
  id?: string;
  label?: string;
  title?: string;
  name?: string;
  displayTitle?: string;
  categoryTypeCode?: string | null;
  categoryTypeName?: string | null;
  type?: string | number | null;
}

export interface CategoryBadgeProps {
  category: CategoryBadgeCategory;
  label?: React.ReactNode;
  onRemove?: () => void;
  isDisabled?: boolean;
  className?: string;
}

const formatCategoryType = (value?: string | null) =>
  value
    ?.replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

export const getCategoryBadgeLabel = (category: CategoryBadgeCategory) =>
  category.label ||
  category.name ||
  category.title ||
  category.displayTitle ||
  '';

export const getCategoryBadgeTypeLabel = (category: CategoryBadgeCategory) =>
  category.categoryTypeName ||
  formatCategoryType(
    category.categoryTypeCode ||
      (typeof category.type === 'string' ? category.type : undefined)
  );

export const getCategoryBadgeTitle = (category: CategoryBadgeCategory) => {
  const label = getCategoryBadgeLabel(category);
  const typeLabel = getCategoryBadgeTypeLabel(category);

  return typeLabel ? `${label} (${typeLabel})`.trim() : label;
};

export const getCategoryBadgesTitle = (
  categories: ReadonlyArray<CategoryBadgeCategory>
) => categories.map(getCategoryBadgeTitle).filter(Boolean).join(', ');

const CategoryBadge: React.FC<CategoryBadgeProps> = ({
  category,
  label,
  onRemove,
  isDisabled,
  className,
}) => {
  const typeLabel = getCategoryBadgeTypeLabel(category);
  const title = getCategoryBadgeTitle(category);

  return (
    <Chip
      buttonFunc={onRemove}
      className={cx('category-badge', className)}
      color="info"
      isDisabled={isDisabled}
    >
      <span className="category-badge__content" title={title || undefined}>
        <span className="category-badge__label">
          {label ?? getCategoryBadgeLabel(category)}
        </span>
        {typeLabel && (
          <span className="category-badge__type">({typeLabel})</span>
        )}
      </span>
    </Chip>
  );
};

export default CategoryBadge;
